import React, { useEffect, useState } from 'react';
import { Landmark, Loader2, ShieldCheck, ChevronDown, ExternalLink } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const GROUPS = [
  ['Ministros titulares', (m) => m.cargo === 'Ministro' && m.calidad === 'titular'],
  ['Ministros suplentes, interinos o en reemplazo', (m) => m.cargo === 'Ministro' && m.calidad !== 'titular'],
  ['Fiscales judiciales', (m) => m.cargo === 'Fiscal Judicial'],
  ['Abogados integrantes', (m) => m.cargo === 'Abogado Integrante'],
];
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString('es-CL', { day: '2-digit', month: 'long', year: 'numeric' }) : '');

const Members = ({ title, list, testid }) => (
  <div data-testid={testid}>
    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{title} · {list.length}</p>
    <p className="mt-0.5 text-xs leading-relaxed text-slate-300">{list.map((m) => m.name).join(' · ')}</p>
  </div>
);

const Salas = ({ s }) => (
  <div data-testid="court-structure-salas">
    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Salas · constitución del {s.salas_fecha}</p>
    <div className="mt-1 grid grid-cols-1 gap-2 sm:grid-cols-2">
      {s.salas.map((sala) => (
        <div key={sala.numero} data-testid={`court-sala-${sala.numero}`} className="rounded-lg border border-slate-700 bg-slate-900/60 p-2">
          <p className="text-xs font-semibold text-amber-200">{sala.nombre}</p>
          {sala.integrantes.map((m, i) => <p key={i} className="text-[11px] text-slate-300">{m.name} <span className="text-slate-500">· {m.cargo}</span></p>)}
        </div>
      ))}
    </div>
    <p className="mt-1 text-[10px] text-slate-500">Instalación provisional publicada por el Poder Judicial (art. 90 COT). La presidencia de cada sala no figura en la fuente oficial, por eso no se muestra.</p>
  </div>
);

// Current, officially verified structure of a court: presidency, members by quality, salas, source and date
export const CourtStructure = ({ slug }) => {
  const { api } = useAuth();
  const [s, setS] = useState(null);
  const [hist, setHist] = useState(false);
  useEffect(() => { setS(null); api.get(`/courts/${slug}/structure`).then((r) => setS(r.data)).catch(() => setS({ error: true })); }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!s) return <p className="flex items-center gap-2 text-xs text-slate-400"><Loader2 className="h-3 w-3 animate-spin" />Cargando estructura del tribunal...</p>;
  if (s.error) return <p className="text-xs text-red-300">No se pudo cargar la estructura del tribunal.</p>;
  const members = s.members.filter((m) => m.cargo !== 'Presidente');
  const isCourt = ['corte_suprema', 'corte_apelaciones'].includes(s.court.level);
  return (
    <div data-testid="court-structure" className="space-y-3 rounded-xl border border-slate-700 bg-slate-800/40 p-3">
      <div className="flex items-start gap-2">
        <Landmark className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-white">{s.court.name}</p>
          {isCourt && (s.president
            ? <p data-testid="court-structure-president" className="text-xs text-slate-300">Presidencia: <b className="text-amber-200">{s.president.name}</b></p>
            : <p data-testid="court-structure-president-missing" className="text-xs text-slate-500">Presidencia: no publicada en la nómina oficial vigente; se mostrará cuando el Poder Judicial la confirme.</p>)}
        </div>
      </div>
      {members.length > 0 ? GROUPS.map(([t, fn], i) => { const list = members.filter(fn); return list.length ? <Members key={t} title={t} list={list} testid={`court-structure-group-${i}`} /> : null; })
        : <p data-testid="court-structure-no-members" className="text-xs text-slate-500">Sin nómina oficial verificada para este tribunal.</p>}
      {s.salas.length > 0 && <Salas s={s} />}
      {s.verified_at && (
        <p data-testid="court-structure-source" className="flex flex-wrap items-center gap-1 text-[10px] text-slate-500">
          <ShieldCheck className="h-3 w-3 text-emerald-400" />Fuente: Poder Judicial de Chile{s.source_url && <a href={s.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 text-slate-400 hover:text-amber-300">pjud.cl<ExternalLink className="h-2.5 w-2.5" /></a>} · verificado el {fmt(s.verified_at)}
        </p>
      )}
      {s.history.length > 0 && (
        <div>
          <button type="button" data-testid="court-structure-history-toggle" onClick={() => setHist((v) => !v)} className="flex items-center gap-1 text-[11px] text-slate-400 hover:text-amber-300">
            <ChevronDown className={`h-3 w-3 transition-transform ${hist ? 'rotate-180' : ''}`} />Retirados de la nómina vigente ({s.history.length})
          </button>
          {hist && <ul data-testid="court-structure-history" className="mt-1 space-y-0.5">{s.history.map((h, i) => <li key={i} className="text-[11px] text-slate-500">{h.name} · retirado el {fmt(h.at)}</li>)}</ul>}
        </div>
      )}
    </div>
  );
};
